/*
 * tagPalette.js — the fixed tag colour palette (WR-010).
 *
 * A tag stores a palette id, never a colour. Each id resolves to a
 * CSS-variable pair — `--tag-<id>-bg` and `--tag-<id>-fg` — declared once
 * per theme in Tags.css. That is what keeps a chip legible in all five
 * themes without a component ever naming a colour, and what makes the
 * palette re-tunable in one stylesheet instead of a database migration.
 *
 * The identical id list lives at backend/utils/tagPalette.js — the two
 * containers share no module, so it is duplicated on purpose. Keep them in
 * step if a colour is ever added or removed.
 */

export const TAG_COLORS = [
  { id: 'slate',  label: 'Slate'  },
  { id: 'red',    label: 'Red'    },
  { id: 'orange', label: 'Orange' },
  { id: 'lime',   label: 'Lime'   },
  { id: 'green',  label: 'Green'  },
  { id: 'teal',   label: 'Teal'   },
  { id: 'cyan',   label: 'Cyan'   },
  { id: 'blue',   label: 'Blue'   },
  { id: 'indigo', label: 'Indigo' },
  { id: 'violet', label: 'Violet' },
  { id: 'purple', label: 'Purple' },
  { id: 'pink',   label: 'Pink'   },
];

const TAG_COLOR_IDS = TAG_COLORS.map(c => c.id);

export const isTagColor = (c) => TAG_COLOR_IDS.includes(c);

/** Fall back to a real palette id so a chip can never render uncoloured. */
export const safeTagColor = (c) => (isTagColor(c) ? c : 'slate');

/**
 * A colour to suggest for a new tag.
 *
 * Prefers one the project isn't using yet, so a project's first twelve tags
 * come out visually distinct instead of landing on the same few colours by
 * chance. Once all twelve are in use it picks at random.
 *
 * The server suggests one too (it owns the authoritative tag list); this is
 * for the picker, which needs a swatch pre-selected before it has asked.
 */
export function suggestTagColor(usedColors = []) {
  const used = new Set(usedColors.filter(isTagColor));
  const free = TAG_COLOR_IDS.filter(c => !used.has(c));
  const pool = free.length > 0 ? free : TAG_COLOR_IDS;
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Resolve a task's tag ids against the project's tag list, in list order. */
export function resolveTags(project, tagIds) {
  if (!tagIds?.length || !project?.tags?.length) return [];
  const wanted = new Set(tagIds.map(t => String(t?._id || t)));
  return project.tags.filter(t => wanted.has(String(t._id)));
}
